import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import {
    TableProvider,
    TableHeader,
    Table,
    TableActiveColumnFilters,
    IconName,
    Stack,
    StackItem,
} from '@optimus-web/core';
import { OptimusProvider } from '@mews-commander/ui';
import { LocalizedProperty, lt, useUrlSyncedTableState } from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import { RouteName, ActionType, ArticlePath, createGuideLink } from '@mews-commander/enums';
import { WithServiceId } from '@mews-commander/types';
import { RateOverview, RateOverviewFilters } from '@mews-commander/data-access';
import { useCommanderSelector } from '@mews-commander/utils';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { getServiceNames } from '../../billing/selectors';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import { RatesQuotaProgressChart } from '../components/RatesQuotaProgressChart';
import { useGetRatesOverviewEntries } from '../queries';
import { RateSidePanelContextProvider } from '../components/contexts/RateSidePanelContext';
import { CurrentServiceIdContextProvider } from '../components/contexts/CurrentServiceIdContext';
import { useCreateRateDialogForm } from '../components/forms/hooks/useCreateRateDialogForm';
import { BaseLayout } from '../../../components/layout';
import { columns } from './columns';

export const RatesOverviewTable = ({ serviceId }: WithServiceId) => {
    const [tableState, setTableState] = useUrlSyncedTableState<RateOverview, RateOverviewFilters>({
        columns,
        pageSize: 100,
        extraQuery: {
            OwnerId: serviceId,
        },
    });

    const { data, isFetching } = useGetRatesOverviewEntries(tableState, serviceId);

    const { name: currentEnterpriseName, id: currentEnterpriseId } = useCurrentEnterprise();

    const resolveRoute = useResolveRoute();

    const serviceName = useCommanderSelector((state) => getServiceNames(state, { id: serviceId })) as Record<
        string,
        string
    >;

    const breadcrumbs: Breadcrumbs = [
        {
            text: currentEnterpriseName,
            url: resolveRoute(RouteName.EnterpriseDetail, { enterpriseId: currentEnterpriseId }),
        },
        {
            text: lt('ServicePlural'),
            url: resolveRoute(RouteName.ServiceIndex, { query: { OwnerId: currentEnterpriseId } }),
        },
        {
            text: <LocalizedProperty property={serviceName} />,
            url: resolveRoute(RouteName.VisitDetail, { serviceId }),
        },
        { text: lt('RatePlural'), active: true },
    ];

    const guideLink = createGuideLink(ArticlePath.CreateRate);

    const [renderCreateDialog, toggleCreateDialog] = useCreateRateDialogForm();

    const rateCreationActionItem: HeadingAction = {
        icon: IconName.Add,
        'data-test-icon-button': IconName.Add,
        text: lt('CreateRate'),
        type: ActionType.Link,
        onClick: toggleCreateDialog,
    };

    const rateActions: HeadingAction[] = [
        rateCreationActionItem,
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: lt('Help'),
            type: ActionType.Link,
            actionId: 'help',
        },
    ];

    return (
        <OptimusProvider density="spacious">
            <BaseLayout breadcrumbs={breadcrumbs} actions={rateActions} padded>
                <CurrentServiceIdContextProvider value={serviceId}>
                    <RateSidePanelContextProvider>
                        <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                            <Stack distribution="space-between">
                                <TableHeader title={lt('RatePlural')} subTitle={data?.totalRowCount || '0'} />
                                <StackItem style={{ paddingBottom: '2em', minWidth: '300px' }}>
                                    <RatesQuotaProgressChart serviceId={serviceId} />
                                </StackItem>
                            </Stack>
                            <TableActiveColumnFilters />
                            <Table
                                uniqueRowId="id"
                                data={data?.data ?? []}
                                loading={isFetching}
                                amountOfRows={data?.totalRowCount ?? 0}
                                fixedFirstColumn
                                fixedLastColumn
                            />
                        </TableProvider>
                        {renderCreateDialog()}
                    </RateSidePanelContextProvider>
                </CurrentServiceIdContextProvider>
            </BaseLayout>
        </OptimusProvider>
    );
};
